import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { Maximize2, Minimize2, EllipsisVertical, Download, TableProperties, ChartLine, ImageDown } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import { selectedDeviceSelector } from '../../store/api/devices/selectors';
import { IFullDeviceDetails } from '../../store/api/deviceConfig/types';
import { usePerformanceFilters } from '../../pages/DeviceDetail/Performance/PerformanceContext';
import { ChartColors, getThemeAwareChartColors } from '../../theme/chartColors';
import { useIsDarkMode } from '../../theme/hooks';
import { useTheme } from '../../theme/hooks';
import { LOCAL_STORAGE } from '../../globals/globals';

dayjs.extend(utc);

/**
 * Configuration for chart series (lines, bars, etc.)
 */
export interface ResourcesChartSeriesConfig {
  name: string;
  type: 'bar' | 'line';
  yAxisIndex: 0 | 1;
  color: string;  // Hex color code
  dataKey: string;
  isPercentage?: boolean;
  dataTransform?: (value: number) => number;  // Optional data transformation function
}

/**
 * Configuration for Y-axis
 */
export interface ResourcesChartAxisConfig {
  name: string;
  position: 'left' | 'right';
  min?: number;
  max?: number;
  autoMax?: {
    floor: number;
    step: number;
  };
  formatter?: string;
  nameGap?: number;
}

/**
 * Main configuration object for Resources charts
 * This is the template structure for all charts in ResourcesP tab
 */
export interface ResourcesChartConfig {
  title: string;
  apiEndpoint: string;
  csvFilename: string;
  chartImageFilename: string;
  series: ResourcesChartSeriesConfig[];
  yAxes: [ResourcesChartAxisConfig, ResourcesChartAxisConfig];
  tableColumns: {
    header: string;
    dataKey: string;
    isPercentage?: boolean;
    dataTransform?: (value: number) => number;  // Optional data transformation function
  }[];
  timestampField: string;  // Field name for timestamp in API response
}

export interface ResourcesChartGridSize {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

interface ResourcesChartProps {
  config: ResourcesChartConfig;
  onFullscreenToggle: () => void;
  isFullscreen: boolean;
  gridSize?: ResourcesChartGridSize;
}

/**
 * ResourcesChart Component
 * 
 * A reusable chart component specifically designed for ResourcesP tab charts.
 * Supports memory, CPU, and other resource monitoring charts.
 * 
 * Features:
 * - ECharts rendering with smooth animations
 * - Fullscreen mode
 * - CSV export
 * - Chart image export
 * - Table/Chart view toggle
 * - Time range filters
 * - Responsive design
 * 
 * @param config - Chart configuration object (see ResourcesChartConfig)
 * @param onFullscreenToggle - Callback for fullscreen toggle
 * @param isFullscreen - Current fullscreen state
 * @param gridSize - Optional grid sizing for flexible layouts
 */
const ResourcesChart: React.FC<ResourcesChartProps> = ({ 
  config,
  onFullscreenToggle, 
  isFullscreen,
  gridSize
}) => {
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const { timeRange, truncate, startDate, endDate, refreshTrigger } = usePerformanceFilters();
  const isDarkMode = useIsDarkMode();
  const themeColors = useMemo(() => getThemeAwareChartColors(), [isDarkMode]);

  // Resolve a semantic color name (e.g. 'info', 'chart1') or fallback to a raw hex string
  const resolveColor = (color: string): string => {
    if (color in themeColors) {
      return (themeColors as Record<string, string>)[color];
    }
    return color;
  };
  
  // Extract serialNumber to avoid unnecessary re-renders when selectedDevice object reference changes
  const serialNumber = selectedDevice?.serialNumber;
  const { mode } = useTheme();
  const getCssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Use sessionStorage to persist table view preference
  const storageKey = `resources-chart-view-${config.title}`;
  const [showTable, setShowTable] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(storageKey);
    return stored === 'true';
  });
  
  const chartRef = useRef<ReactECharts>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleView = () => {
    const newValue = !showTable;
    setShowTable(newValue);
    sessionStorage.setItem(storageKey, String(newValue));
  };

  // Export chart data as CSV
  const handleExportCSV = () => {
    if (chartData.length === 0) {
      alert('No data available to export');
      return;
    }

    const headers = ['Timestamp', ...config.tableColumns.map(col => col.header)];
    
    const rows = chartData.map(d => {
      const row = [dayjs.utc(d[config.timestampField]).format('YYYY-MM-DD HH:mm:ss')];
      config.tableColumns.forEach(col => {
        let value = d[col.dataKey];
        if (col.dataTransform) {
          value = col.dataTransform(value);
        }
        row.push(col.isPercentage ? value.toFixed(2) : value);
      });
      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.csvFilename}_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    handleMenuClose();
  };

  // Export chart as image
  const handleExportImage = () => {
    if (!chartRef.current) {
      alert('Chart not available');
      return;
    }

    const echartsInstance = chartRef.current.getEchartsInstance();
    const url = echartsInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: themeColors.background
    });

    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.chartImageFilename}_${dayjs().format('YYYY-MM-DD')}.png`;
    a.click();
    handleMenuClose();
  };

  // Fetch chart data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!serialNumber) {
        setError('No device selected');
        setLoading(false);
        return;
      }

      // If custom range is selected but dates are not set, don't fetch
      if (timeRange === 'custom' && (!startDate || !endDate)) {
        return; // Don't fetch, wait for user to select dates
      }

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, string> = {
          site_name: serialNumber,
          truncate: truncate,
        };

        // Add date range parameters based on selection
        if (timeRange === 'custom' && startDate && endDate) {
          params.start_date = startDate.startOf('day').format('YYYY-MM-DD');
          params.end_date = endDate.startOf('day').format('YYYY-MM-DD');
        } else {
          params.time_range = timeRange;
        }

        // Use the gateway proxy path to route through devices-api to python-chart-api
        // This ensures proper authentication and routing through the gateway
        const apiBaseUrl = '/v1/charts';
        
        // Get JWT token from localStorage for authentication
        const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
        
        const response = await fetch(`${apiBaseUrl}${config.apiEndpoint}?${new URLSearchParams(params)}`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.length === 0) {
          console.warn(`${config.title}: No data available for site "${selectedDevice.serialNumber}"`);
        }
        
        setChartData(data);
      } catch (err) {
        console.error('Error fetching chart data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serialNumber, timeRange, truncate, startDate, endDate, config, refreshTrigger]);

  // Prepare ECharts option
  const getOption = () => {
    if (chartData.length === 0) return {};

    const tooltipTsFormat = truncate === 'minute' ? 'MMM DD hh:mm A' : truncate === 'hour' ? 'MMM DD hh:00 A' : 'MMM DD';
    const timestamps = chartData.map(d => dayjs.utc(d[config.timestampField]).format('MMM DD'));
    const tooltipTimestamps = chartData.map(d => dayjs.utc(d[config.timestampField]).format(tooltipTsFormat));
    
    // Prepare series data with Site E styling
    const seriesData = config.series.map(seriesConfig => {
      return {
        name: seriesConfig.name,
        type: seriesConfig.type,
        yAxisIndex: seriesConfig.yAxisIndex,
        data: chartData.map(d => {
          const value = d[seriesConfig.dataKey];
          return seriesConfig.dataTransform ? seriesConfig.dataTransform(value) : value;
        }),
        itemStyle: {
          color: resolveColor(seriesConfig.color)
        },
        ...(seriesConfig.type === 'line' ? {
          smooth: false,
          lineStyle: {
            color: resolveColor(seriesConfig.color),
            width: 2
          },
          symbol: 'circle',
          symbolSize: 6
        } : {}),
        emphasis: {
          focus: 'series'
        }
      };
    });

    const computeAutoMax = (axisIndex: number, floor: number, step: number): number => {
      const values = config.series
        .filter(series => series.yAxisIndex === axisIndex)
        .flatMap(series =>
          chartData
            .map(point => {
              const rawValue = point[series.dataKey];
              const transformedValue = series.dataTransform ? series.dataTransform(rawValue) : rawValue;
              return Number(transformedValue);
            })
            .filter(value => Number.isFinite(value))
        );

      if (values.length === 0) {
        return floor;
      }

      const maxValue = Math.max(...values);
      if (maxValue <= floor) {
        return floor;
      }

      // Always move to the next step above the observed max (e.g. 300 -> 350, 350 -> 400)
      return Math.floor(maxValue / step) * step + step;
    };

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: themeColors.tooltipBg,
        borderColor: themeColors.cardBorder,
        textStyle: { color: themeColors.foreground },
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: themeColors.axisLabel
          }
        },
        formatter: (params: any) => {
          const label = tooltipTimestamps[params[0].dataIndex] ?? params[0].axisValue;
          let result = `<strong>${label}</strong><br/>`;
          params.forEach((param: any) => {
            const seriesConfig = config.series.find(s => s.name === param.seriesName);
            const value = seriesConfig?.isPercentage 
              ? `${param.value.toFixed(2)}%` 
              : param.value.toLocaleString();
            result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
          });
          return result;
        }
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none'
          },
          restore: {}
        },
        iconStyle: {
          color: 'none',
          borderColor: getCssVar('--muted-foreground'),
          borderWidth: 1
        },
        emphasis: {
          iconStyle: {
            color: 'none',
            borderColor: getCssVar('--foreground'),
            borderWidth: 1
          }
        },
        right: 20,
        top: 5
      },
      legend: {
        data: config.series.map(s => s.name),
        top: 35,
        itemGap: 20,
        textStyle: {
          fontSize: 12,
          color: themeColors.foreground,
        }
      },
      grid: {
        left: 80,
        right: 80,
        bottom: 50,
        top: 70,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: timestamps,
        axisPointer: {
          type: 'shadow'
        },
        axisLabel: {
          fontSize: 11,
          color: themeColors.axisLabel
        },
        axisLine: {
          lineStyle: { color: themeColors.gridLine }
        },
        boundaryGap: config.series.some(s => s.type === 'bar')
      },
      yAxis: config.yAxes.map((axis, axisIndex) => ({
        type: 'value',
        name: axis.name,
        position: axis.position,
        nameLocation: 'middle',
        nameGap: axis.nameGap || 50,
        min: axis.min,
        max: axis.autoMax ? computeAutoMax(axisIndex, axis.autoMax.floor, axis.autoMax.step) : axis.max,
        nameTextStyle: {
          fontSize: 12,
          color: themeColors.axisLabel,
          align: 'center'
        },
        nameRotate: 90,
        axisLabel: {
          formatter: axis.formatter || '{value}',
          fontSize: 11,
          color: themeColors.axisLabel
        },
        axisLine: {
          lineStyle: { color: themeColors.gridLine }
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: themeColors.gridLine
          }
        }
      })),
      series: seriesData,
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100
        },
        {
          type: 'slider',
          start: 0,
          end: 100,
          height: 20,
          bottom: 10
        }
      ]
    };
  };

  return (
    <div 
      className="rounded-xl border border-card-border bg-card text-card-foreground shadow-sm px-4 pb-4 pt-2"
      style={{
        height: isFullscreen ? Math.min(window.innerHeight - 250, 700) : 'auto',
      }}
    >
      {/* Header with title and actions */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-foreground">
          {config.title}
        </h2>
        <div className="flex gap-1">
          <button
            onClick={onFullscreenToggle}
            className="h-8 w-8 flex items-center justify-center rounded-md bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            onClick={handleMenuOpen}
            className="h-8 w-8 flex items-center justify-center rounded-md bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="chart options"
            aria-controls={menuOpen ? 'chart-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
          >
            <EllipsisVertical className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Menu
        id="chart-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'chart-options-button',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'var(--color-popover, var(--popover))',
              color: 'var(--color-foreground, var(--foreground))',
              borderColor: 'var(--color-border, var(--border))',
              border: '1px solid',
            }
          }
        }}
      >
        <MenuItem onClick={() => { handleToggleView(); handleMenuClose(); }} sx={{ color: 'inherit' }}>
          {showTable ? <ChartLine className="h-4 w-4 mr-2" /> : <TableProperties className="h-4 w-4 mr-2" />}
          {showTable ? 'Show as Chart' : 'Show as Table'}
        </MenuItem>
        <MenuItem onClick={() => { handleExportCSV(); handleMenuClose(); }} disabled={chartData.length === 0} sx={{ color: 'inherit' }}>
          <Download className="h-4 w-4 mr-2" />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={() => { handleExportImage(); handleMenuClose(); }} disabled={chartData.length === 0 || showTable} sx={{ color: 'inherit' }}>
          <ImageDown className="h-4 w-4 mr-2" />
          Save as Image
        </MenuItem>
      </Menu>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center" style={{ height: 400 }}>
          <CircularProgress sx={{ color: 'var(--color-primary, var(--primary))' }} />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && chartData.length === 0 && (
        <div className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          No data available for the selected time range
        </div>
      )}

      {/* Chart View */}
      {!loading && !error && chartData.length > 0 && !showTable && (
        <ReactECharts
          ref={chartRef}
          option={getOption()}
          style={{ height: isFullscreen ? Math.min(window.innerHeight - 350, 600) : 400 }}
          theme="default"
          notMerge={true}
          lazyUpdate={true}
        />
      )}

      {/* Table View */}
      {!loading && !error && chartData.length > 0 && showTable && (
        <div className="overflow-auto rounded-md border border-border" style={{ maxHeight: 400 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-foreground border-b border-border">Timestamp</th>
                {config.tableColumns.map((col, idx) => (
                  <th key={idx} className="px-3 py-2 text-right font-semibold text-foreground border-b border-border">{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, index) => (
                <tr key={index} className="hover:bg-accent/50 transition-colors">
                  <td className="px-3 py-2 text-foreground border-b border-border">{dayjs.utc(row[config.timestampField]).format('MMM DD, YYYY HH:mm')}</td>
                  {config.tableColumns.map((col, idx) => {
                    let value = row[col.dataKey];
                    if (col.dataTransform) {
                      value = col.dataTransform(value);
                    }
                    return (
                      <td key={idx} className="px-3 py-2 text-right text-foreground border-b border-border">
                        {col.isPercentage 
                          ? `${value.toFixed(2)}%` 
                          : value.toLocaleString()}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ResourcesChart;
